/**
 * Анализ конкурентов, как его показывает экран (`widgets/competitors/CompetitorAnalysis.tsx`).
 *
 * Источник формулы для бэкенд-порта `backend/services/competitor_screen_analysis.py`
 * и golden-фикстур `tests/golden/competitor_screen.json`. Не путать с
 * `backend/services/competitor_analysis.py` — другим алгоритмом внутри `POST /api/calculate`.
 */

/** Keepa не отдала referral fee — экран подставляет типовые 15 %. */
export const DEFAULT_REFERRAL_FEE_PERCENT = 15;
/**
 * «Ниже рынка» — дешевле 90 % средней цены, «выше» — дороже 110 %.
 * Множители буквальные (не `1 ± 0.1`): у бэкенд-порта те же литералы, и double-результат
 * сравнения совпадает побитово.
 */
export const PRICE_POSITION_LOW = 0.9;
export const PRICE_POSITION_HIGH = 1.1;

export type MarketPosition = 'low' | 'average' | 'high';

export interface CompetitorKeepaInput {
  asin: string;
  price: number;
  monthlySales: number;
  fbaFee: number;
  /** `null` — Keepa не отдала; экран подставляет `DEFAULT_REFERRAL_FEE_PERCENT`. */
  referralFeePercent: number | null;
}

export interface CompetitorDetails {
  asin: string;
  price: number;
  monthlySales: number;
  fbaFee: number;
  referralFee: number;
  referralFeePercent: number;
  netRevenue: number;
  totalCost: number;
  marketShare: number;
  profit: number;
  /** Прибыль, если бы у конкурента был наш COGS. */
  estimatedProfit: number;
  estimatedMargin: number;
  isOurs: boolean;
}

export interface CompetitorScreenAnalysis {
  competitorCount: number;
  avgMarketPrice: number;
  totalMarketSales: number;
  ourForecast: number;
  priceAdvantage: number;
  marketPosition: MarketPosition;
  competitors: CompetitorDetails[];
}

export function analyzeCompetitorScreen(
  items: CompetitorKeepaInput[],
  mainAsin: string | null,
  ourPrice: number,
  ourCogs: number,
): CompetitorScreenAnalysis {
  const competitors: CompetitorDetails[] = [];
  let totalMarketSales = 0;
  let totalPriceSum = 0;
  let priceCount = 0;

  for (const item of items) {
    const price = item.price || 0;
    const monthlySales = item.monthlySales || 0;
    const fbaFee = item.fbaFee || 0;
    const referralFeePercent = item.referralFeePercent || DEFAULT_REFERRAL_FEE_PERCENT;
    const referralFeeAmount = price * (referralFeePercent / 100);
    const netRevenue = price - referralFeeAmount - fbaFee;
    const totalCost = referralFeeAmount + fbaFee;

    totalMarketSales += monthlySales;
    if (price > 0) {
      totalPriceSum += price;
      priceCount++;
    }

    const estimatedProfit = netRevenue - ourCogs;
    const estimatedMargin = price > 0 ? (estimatedProfit / price) * 100 : 0;

    competitors.push({
      asin: item.asin,
      price,
      monthlySales,
      fbaFee,
      referralFee: referralFeeAmount,
      referralFeePercent,
      netRevenue,
      totalCost,
      marketShare: 0,
      profit: 0,
      estimatedProfit,
      estimatedMargin,
      isOurs: item.asin === mainAsin,
    });
  }

  for (const comp of competitors) {
    comp.marketShare = totalMarketSales > 0 ? (comp.monthlySales / totalMarketSales) * 100 : 0;
    // Чужой COGS неизвестен — прибыль показывается только у нашего ASIN.
    comp.profit = comp.isOurs ? comp.netRevenue - ourCogs : 0;
  }

  const avgMarketPrice = priceCount > 0 ? totalPriceSum / priceCount : 0;
  const priceAdvantage = avgMarketPrice - ourPrice;
  const competitorCount = competitors.filter((c) => !c.isOurs).length;

  // Наш прогноз — среднее продаж всех ASIN (основной + конкуренты).
  const allSales = competitors.map((c) => c.monthlySales);
  const ourForecast = allSales.length > 0
    ? Math.round(allSales.reduce((a, b) => a + b, 0) / allSales.length)
    : 0;

  let marketPosition: MarketPosition = 'average';
  if (ourPrice < avgMarketPrice * PRICE_POSITION_LOW) {
    marketPosition = 'low';
  } else if (ourPrice > avgMarketPrice * PRICE_POSITION_HIGH) {
    marketPosition = 'high';
  }

  return {
    competitorCount,
    avgMarketPrice,
    totalMarketSales,
    ourForecast,
    priceAdvantage,
    marketPosition,
    competitors: competitors.sort((a, b) => {
      if (a.isOurs) return 1;
      if (b.isOurs) return -1;
      return b.monthlySales - a.monthlySales;
    }),
  };
}
